'use client';

import {useState, useEffect} from 'react';
import {usePathname, useSearchParams} from 'next/navigation';
import {Suspense} from 'react';
import {TheLoopExperienceSubmissionHeader} from 'iq-interview_experiences/components/submit_header';
import {SubmissionSource} from 'iq-interview_experiences/types';
import {Header, HeaderMainNav} from '@repo/ui/header';
import {Dropdown} from '@repo/ui/dropdown';
import {NavLink} from '@repo/ui/nav-link';
import {SearchButton} from '@repo/ui/search-button';
import {HeaderUserNav} from './header_user_nav';
import {useOmniSearch} from 'iq-omnisearch/context';
import {useAuth} from 'iq-auth';
import {
  MissionControlIcon,
  GaugeIcon,
  ClipboardIcon,
  ChatIcon,
  GroupIcon,
  OpenedBookIcon,
  PaperIcon,
  ReceiptIcon,
} from '@repo/ui/icons';
import {FRONTEND_URL} from 'config';
import {API} from 'common/api';

type LearningPathNavItem = {
  title: string;
  slug: string;
  icon_img: string;
  nav_description: string;
  nav_exists: boolean;
};

type LearningPathsCache = {
  learningPaths: LearningPathNavItem[];
  timestamp: number;
};

const CACHE_KEY = 'learning-paths-nav';
const SIX_HOURS = 1000 * 60 * 60 * 6;

function readCache(): LearningPathsCache {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return JSON.parse(raw) as LearningPathsCache;
  } catch {}
  return {learningPaths: [], timestamp: 0};
}

function writeCache(data: LearningPathsCache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {}
}

function MenuIcon() {
  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M3 18H21'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M3 12H21'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M3 6H21'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M18 6L6 18'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M6 6L18 18'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function MobilePlusIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M12 5V19'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M5 12H19'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function MobileMinusIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M5 12H19'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

type MobileSection = 'practice' | 'learning-paths' | 'simulate' | 'resources';

function MobileSection({
  label,
  href,
  id,
  open,
  onToggle,
  children,
}: {
  label: string;
  href: string;
  id: MobileSection;
  open: boolean;
  onToggle: (id: MobileSection) => void;
  children: React.ReactNode;
}) {
  return (
    <div className='border-b border-gray-100'>
      <div
        className='flex items-center justify-between py-3 cursor-pointer'
        onClick={() => onToggle(id)}>
        <span className='text-sm font-medium text-iq-grey-dark'>{label}</span>
        <span className='text-iq-grey-dark'>{open ? <MobileMinusIcon /> : <MobilePlusIcon />}</span>
      </div>
      {open && (
        <div className='pb-2 flex flex-col gap-1'>
          <a
            href={href}
            className='px-2 py-1.5 text-sm text-iq-grey-dark hover:text-iq-black hover:bg-iq-grey-lighter rounded'>
            All {label}
          </a>
          {children}
        </div>
      )}
    </div>
  );
}

function MobileNavLink({label, href}: {label: string; href: string}) {
  return (
    <a
      href={href}
      className='px-2 py-1.5 text-sm text-iq-grey-dark hover:text-iq-black hover:bg-iq-grey-lighter rounded'>
      {label}
    </a>
  );
}

function MobileMenuPanel({
  showPricing,
  learningPaths,
}: {
  showPricing: boolean;
  learningPaths: LearningPathNavItem[];
}) {
  const [openSection, setOpenSection] = useState<MobileSection | null>(null);

  const toggle = (id: MobileSection) => setOpenSection((prev) => (prev === id ? null : id));

  return (
    <div className='min-[1170px]:hidden bg-white border-t border-gray-100 shadow-md relative z-399'>
      <nav className='flex flex-col px-5'>
        <MobileSection
          label='Practice'
          href={`${FRONTEND_URL}/questions`}
          id='practice'
          open={openSection === 'practice'}
          onToggle={toggle}>
          <MobileNavLink label='Interview Questions' href={`${FRONTEND_URL}/questions`} />
          <MobileNavLink label='Challenges' href={`${FRONTEND_URL}/challenges`} />
          <MobileNavLink label='Takehomes' href={`${FRONTEND_URL}/takehomes`} />
        </MobileSection>

        <MobileSection
          label='Learning Paths'
          href={`${FRONTEND_URL}/learning-paths`}
          id='learning-paths'
          open={openSection === 'learning-paths'}
          onToggle={toggle}>
          {learningPaths
            .filter((lp, i) => lp.nav_exists && i < 7)
            .map((lp) => (
              <MobileNavLink
                key={lp.slug}
                label={lp.title}
                href={`${FRONTEND_URL}/learning-paths/${lp.slug}`}
              />
            ))}
        </MobileSection>

        <MobileSection
          label='Simulate'
          href={`${FRONTEND_URL}/mock-interviews`}
          id='simulate'
          open={openSection === 'simulate'}
          onToggle={toggle}>
          <MobileNavLink label='Mock Interviews' href={`${FRONTEND_URL}/mock-interviews`} />
          <MobileNavLink label='Coaching' href={`${FRONTEND_URL}/coaching`} />
          <MobileNavLink label='AI Interviewer' href={`${FRONTEND_URL}/ai-mock-interview`} />
        </MobileSection>

        <a
          href={`${FRONTEND_URL}/companies`}
          className='py-3 text-sm font-medium text-iq-grey-dark border-b border-gray-100 hover:text-iq-black'>
          Interview Guides
        </a>

        <MobileSection
          label='Resources'
          href={`${FRONTEND_URL}/blog`}
          id='resources'
          open={openSection === 'resources'}
          onToggle={toggle}>
          <MobileNavLink label='Blog' href={`${FRONTEND_URL}/blog`} />
          <MobileNavLink label='Salaries' href={`${FRONTEND_URL}/salaries`} />
          <MobileNavLink label='Job Board' href={`${FRONTEND_URL}/jobs`} />
        </MobileSection>

        {showPricing && (
          <a
            href={`${FRONTEND_URL}/pricing`}
            className='py-3 text-sm font-medium text-iq-grey-dark hover:text-iq-black'>
            Pricing
          </a>
        )}
      </nav>
      <div className='px-5 py-4 border-t border-gray-100'>
        <HeaderUserNav />
      </div>
    </div>
  );
}

function SubmitPageHeader() {
  const searchParams = useSearchParams();
  if (searchParams.get('source') === SubmissionSource.TheLoop) {
    return <TheLoopExperienceSubmissionHeader />;
  }
  return <FullSiteHeader />;
}

export function SiteHeader() {
  const pathname = usePathname();

  if (pathname === '/submit-experience') {
    return (
      <Suspense fallback={null}>
        <SubmitPageHeader />
      </Suspense>
    );
  }

  return <FullSiteHeader />;
}

function FullSiteHeader() {
  const {openSearch} = useOmniSearch();
  const {user} = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [learningPaths, setLearningPaths] = useState<LearningPathNavItem[]>([]);

  useEffect(() => {
    const now = Date.now();
    const cached = readCache();
    if (cached.timestamp > now - SIX_HOURS && cached.learningPaths.length > 0) {
      setLearningPaths(cached.learningPaths);
      return;
    }
    API.get('/v1/learning-paths?mini=true')
      .then((data: LearningPathNavItem[]) => {
        setLearningPaths(data);
        writeCache({learningPaths: data, timestamp: now});
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <Header logoHref={FRONTEND_URL} sticky={false}>
        <HeaderMainNav>
          <Dropdown label='Practice' href={`${FRONTEND_URL}/questions`}>
            <NavLink
              label='Interview Questions'
              description='Practice data science interview questions asked in actual interviews from top companies.'
              icon={<MissionControlIcon color='#155e75' />}
              href={`${FRONTEND_URL}/questions`}
            />
            <NavLink
              label='Challenges'
              description='Test your wit against other users and see how your skills compare.'
              icon={<GaugeIcon color='#155e75' />}
              href={`${FRONTEND_URL}/challenges`}
            />
            <NavLink
              label='Takehomes'
              description='Jumpstart your projects in a step-by-step fashion through takehomes from top tech companies.'
              icon={<ClipboardIcon color='#155e75' />}
              href={`${FRONTEND_URL}/takehomes`}
            />
          </Dropdown>
          <Dropdown label='Learning Paths' href={`${FRONTEND_URL}/learning-paths`}>
            <NavLink
              label='All Learning Paths'
              description='Explore different learning paths to help you prepare for your next interview.'
              icon={<OpenedBookIcon color='#16a34a' />}
              href={`${FRONTEND_URL}/learning-paths`}
            />
            {learningPaths
              .filter((lp, i) => lp.nav_exists && i < 7)
              .map((lp) => (
                <NavLink
                  key={lp.slug}
                  label={lp.title}
                  description={lp.nav_description}
                  href={`${FRONTEND_URL}/learning-paths/${lp.slug}`}
                  icon={
                    <img src={lp.icon_img} width={24} height={24} alt={lp.title} loading='lazy' />
                  }
                />
              ))}
          </Dropdown>
          <Dropdown label='Simulate' href={`${FRONTEND_URL}/mock-interviews`}>
            <NavLink
              label='Mock Interviews'
              description='Hone your interviewing skills with your peers with mock interview sessions.'
              icon={<ChatIcon color='#155e75' />}
              href={`${FRONTEND_URL}/mock-interviews`}
            />
            <NavLink
              label='Coaching'
              description='Connect with our curated list of top industry professionals and ace your next data science interview!'
              icon={<GroupIcon color='#155e75' />}
              href={`${FRONTEND_URL}/coaching`}
            />
            <NavLink
              label='AI Interviewer'
              description='Practice interviews with an AI interviewer and get instant feedback.'
              icon={<MissionControlIcon color='#155e75' />}
              href={`${FRONTEND_URL}/ai-mock-interview`}
            />
          </Dropdown>
          <Dropdown label='Interview Guides' href={`${FRONTEND_URL}/companies`} />
          <Dropdown label='Resources' href={`${FRONTEND_URL}/blog`}>
            <NavLink
              label='Blog'
              description='Keep up-to-date with industry trends and must-knows, or read interesting data science topics with Interview Query blogs.'
              icon={<PaperIcon color='#155e75' />}
              href={`${FRONTEND_URL}/blog`}
            />
            <NavLink
              label='Salaries'
              description='View salaries based on location, company, and seniority.'
              icon={<ReceiptIcon color='#155e75' />}
              href={`${FRONTEND_URL}/salaries`}
            />
            <NavLink
              label='Job Board'
              description='Find your next dream job on our data science job board.'
              icon={<ReceiptIcon color='#155e75' />}
              href={`${FRONTEND_URL}/jobs`}
            />
          </Dropdown>
          {!user.hasSubscription && (
            <Dropdown label='Pricing' alignment='right' href={`${FRONTEND_URL}/pricing`} />
          )}
          <SearchButton onClick={openSearch} />
        </HeaderMainNav>

        {/* Desktop user nav — hidden on mobile */}
        <div className='hidden min-[1170px]:flex items-center'>
          <HeaderUserNav />
        </div>

        {/* Mobile: search + hamburger toggle — hidden on desktop */}
        <div className='flex min-[1170px]:hidden items-center gap-2'>
          <SearchButton onClick={openSearch} />
          <button
            onClick={() => setShowMobileMenu((v) => !v)}
            className='p-1 text-iq-grey-dark'
            aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}>
            {showMobileMenu ? <XIcon /> : <MenuIcon />}
          </button>
        </div>
      </Header>

      {showMobileMenu && (
        <MobileMenuPanel showPricing={!user.hasSubscription} learningPaths={learningPaths} />
      )}
    </>
  );
}
